import { debounce } from 'lodash';
import { useMemo, useState, ReactNode, useEffect } from 'react';

import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import Checkbox from '@mui/material/Checkbox';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TextField from '@mui/material/TextField';
import TableContainer from '@mui/material/TableContainer';
import TableSortLabel from '@mui/material/TableSortLabel';
import CircularProgress from '@mui/material/CircularProgress';

import { useInfiniteScroll } from 'src/hooks/use-infinite-scroll';

import { SortConfig } from 'src/services/api.types';
import { SortOrder } from 'src/services/common.types';

import Scrollbar from 'src/components/scrollbar';
import { TableNoData, TableSkeleton } from 'src/components/table';

import { ColumnDef } from './data-list.types';

// ----------------------------------------------------------------------

interface DataListProps<TRow> {
  /** Already resolved to the visible, ordered set (see resolveColumns). */
  columns: ColumnDef<TRow>[];
  rows: TRow[];
  getRowId: (row: TRow) => string;
  /** True while any query (initial, next page, or re-filter) is in flight. */
  isSearching: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  sort: SortConfig;
  onSortChange: (sort: SortConfig) => void;
  columnSearch: Record<string, string>;
  onColumnSearchChange: (key: string, value: string) => void;
  /** Caller-owned toolbar (global search, create/import buttons, the Columns picker trigger). */
  toolbar?: ReactNode;
  selectable?: boolean;
  selectedIds?: string[];
  onSelectRow?: (id: string) => void;
  onSelectAll?: (checked: boolean, ids: string[]) => void;
  /** Leading per-row cell before the configurable columns (e.g. the record-panel eye, JT1-1529). */
  renderRowLeading?: (row: TRow) => ReactNode;
  /** Trailing per-row actions cell (kebab menu etc.) — owned by the consumer. */
  renderRowActions?: (row: TRow) => ReactNode;
  emptyLabel?: string;
  /**
   * Rendered in place of the empty state when the REQUEST failed rather than matched nobody
   * (JT1-1647). A failed search and a search that found nothing are different answers, and the list
   * has no way to tell them apart from `rows` alone — so the caller says which one this is.
   */
  errorContent?: ReactNode;
  searchPlaceholder?: string;
}

// ----------------------------------------------------------------------

/** Per-column search input with local state and a ~300ms debounce so typing doesn't fire a request per keystroke. */
function ColumnSearchInput({
  initialValue,
  placeholder,
  onChange,
}: {
  initialValue: string;
  placeholder: string;
  onChange: (value: string) => void;
}) {
  const [value, setValue] = useState(initialValue);

  const debounced = useMemo(() => debounce((v: string) => onChange(v), 300), [onChange]);
  useEffect(() => () => debounced.cancel(), [debounced]);

  // Keep in sync if the value is reset externally (e.g. Clear filters).
  useEffect(() => setValue(initialValue), [initialValue]);

  return (
    <TextField
      size="small"
      fullWidth
      value={value}
      placeholder={placeholder}
      onChange={(e) => {
        setValue(e.target.value);
        debounced(e.target.value);
      }}
      inputProps={{ 'aria-label': placeholder }}
      sx={{ '& .MuiInputBase-input': { py: 0.5, fontSize: 13 } }}
    />
  );
}

// ----------------------------------------------------------------------

/**
 * Registry-driven list surface shared by the candidates/contacts/companies lists (JT1-1528). Owns
 * the list MECHANICS — visible/ordered columns, per-column header sort (server), a per-column search
 * row, row selection, infinite scroll, and the searching/empty states. Domain concerns (row actions,
 * the toolbar, the column picker) are passed in as slots so the intricate per-entity wiring stays
 * with the consumer and this component never has to know about it.
 *
 * A custom header (not TableHeadCustom) is used deliberately: TableHeadCustom makes every column
 * sortable or none, but item 4 needs per-column sortability.
 */
export default function DataList<TRow>({
  columns,
  rows,
  getRowId,
  isSearching,
  hasMore,
  onLoadMore,
  sort,
  onSortChange,
  columnSearch,
  onColumnSearchChange,
  toolbar,
  selectable = false,
  selectedIds = [],
  onSelectRow,
  onSelectAll,
  renderRowLeading,
  renderRowActions,
  emptyLabel,
  errorContent,
  searchPlaceholder = 'Search',
}: DataListProps<TRow>) {
  const sentinelRef = useInfiniteScroll<HTMLTableRowElement>({ hasMore, isLoading: isSearching, onLoadMore });

  const rowIds = rows.map(getRowId);
  const allSelected = rowIds.length > 0 && rowIds.every((id) => selectedIds.includes(id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const handleSort = (key: string) => {
    const isActive = sort.sort_by === key;
    const nextOrder = isActive && sort.sort_order === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
    onSortChange({ sort_by: key, sort_order: nextOrder });
  };

  const colSpan = columns.length + (selectable ? 1 : 0) + (renderRowLeading ? 1 : 0) + (renderRowActions ? 1 : 0);
  const hasColumnSearch = columns.some((col) => col.searchable);
  const notFound = !isSearching && rows.length === 0;

  return (
    <Box>
      {toolbar}

      {isSearching && rows.length === 0 ? (
        <TableSkeleton />
      ) : (
        <TableContainer sx={{ overflow: 'unset' }}>
          <Scrollbar>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  {selectable && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={someSelected}
                        checked={allSelected}
                        onChange={(e) => onSelectAll?.(e.target.checked, rowIds)}
                      />
                    </TableCell>
                  )}

                  {renderRowLeading && <TableCell padding="checkbox" />}

                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.align || 'left'}
                      sortDirection={sort.sort_by === col.key ? sort.sort_order : false}
                      sx={{ width: col.width, minWidth: col.minWidth }}
                    >
                      {col.sortable ? (
                        <TableSortLabel
                          active={sort.sort_by === col.key}
                          direction={sort.sort_by === col.key ? sort.sort_order : SortOrder.ASC}
                          onClick={() => handleSort(col.key)}
                        >
                          {col.label}
                        </TableSortLabel>
                      ) : (
                        col.label
                      )}
                    </TableCell>
                  ))}

                  {renderRowActions && <TableCell align="right" />}
                </TableRow>

                {hasColumnSearch && (
                  <TableRow>
                    {selectable && <TableCell padding="checkbox" />}
                    {renderRowLeading && <TableCell padding="checkbox" />}
                    {columns.map((col) => (
                      <TableCell key={col.key} sx={{ py: 0.5 }}>
                        {col.searchable ? (
                          <ColumnSearchInput
                            initialValue={columnSearch[col.key] ?? ''}
                            placeholder={searchPlaceholder}
                            onChange={(value) => onColumnSearchChange(col.key, value)}
                          />
                        ) : null}
                      </TableCell>
                    ))}
                    {renderRowActions && <TableCell />}
                  </TableRow>
                )}
              </TableHead>

              <TableBody>
                {rows.map((row) => {
                  const id = getRowId(row);
                  return (
                    <TableRow key={id} hover selected={selectedIds.includes(id)}>
                      {selectable && (
                        <TableCell padding="checkbox">
                          <Checkbox checked={selectedIds.includes(id)} onClick={() => onSelectRow?.(id)} />
                        </TableCell>
                      )}
                      {renderRowLeading && <TableCell padding="checkbox">{renderRowLeading(row)}</TableCell>}
                      {columns.map((col) => (
                        <TableCell key={col.key} align={col.align || 'left'}>
                          {col.render(row)}
                        </TableCell>
                      ))}
                      {renderRowActions && <TableCell align="right">{renderRowActions(row)}</TableCell>}
                    </TableRow>
                  );
                })}

                {/* Infinite-scroll sentinel: a zero-height row observed to trigger the next page. */}
                {hasMore && (
                  <TableRow ref={sentinelRef}>
                    <TableCell colSpan={colSpan} align="center" sx={{ py: 2, border: 0 }}>
                      <CircularProgress size={20} />
                    </TableCell>
                  </TableRow>
                )}

                {errorContent ? (
                  <TableRow>
                    <TableCell colSpan={12}>{errorContent}</TableCell>
                  </TableRow>
                ) : (
                  <TableNoData notFound={notFound} title={emptyLabel} sx={{ py: 6 }} />
                )}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>
      )}
    </Box>
  );
}
